import React, { useState } from 'react';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useApi } from '../../hooks/useApi';
import { addKid } from '../../lib/api/kids';
import { useAuth } from '../../contexts/AuthContext';
import { useError } from '../../contexts/ErrorContext';

export default function AddKidModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const { showError } = useError();
  const { execute, loading } = useApi(addKid);
  
  const [formData, setFormData] = useState({
    name: '',
    balance: '0',
    allowance: '',
    interval: 'weekly',
    allowance_day: 'monday',
    allowance_time: '09:00'
  });

  const handleCurrencyChange = (field, value) => {
    const regex = /^\d*\.?\d{0,2}$/;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleIntervalChange = (e) => {
    const newInterval = e.target.value;
    let newDay = formData.allowance_day;
    if (newInterval === 'weekly') newDay = 'monday';
    if (newInterval === 'monthly') newDay = '1';
    setFormData({ ...formData, interval: newInterval, allowance_day: newDay });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name.trim()) return showError("Name is required");
    
    // Safety Net: Prevent null household crash
    if (!user?.householdId) {
      return showError("Your session lost connection to the household. Please refresh the page and try again.");
    }

    const balanceNum = parseFloat(formData.balance || '0');
    const allowanceNum = formData.allowance ? parseFloat(formData.allowance) : null;
    
    if (isNaN(balanceNum)) return showError("Please enter a valid starting balance");

    try {
      await execute(user.householdId, {
        name: formData.name.trim(),
        balance: balanceNum,
        allowance_amount: allowanceNum,
        allowance_interval: formData.interval,
        allowance_day: (allowanceNum && formData.interval !== 'daily') ? formData.allowance_day : null,
        allowance_time: allowanceNum ? formData.allowance_time : null
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const footer = (
    <>
      <Button variant="ghost" type="button" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" type="button" onClick={handleSubmit} loading={loading} icon="Check">
        Save Profile
      </Button>
    </>
  );

  const hasAllowance = formData.allowance && parseFloat(formData.allowance) > 0;

  return (
    <Modal title="Add Child Profile" onClose={onClose} footer={footer}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input 
          label="Child's Name *" 
          value={formData.name} 
          onChange={e => setFormData({...formData, name: e.target.value})} 
          placeholder="e.g. Alex"
          required
          autoFocus
        />
        
        <Input 
          label="Starting Balance ($) *" 
          type="text" 
          inputMode="decimal"
          value={formData.balance} 
          onChange={e => handleCurrencyChange('balance', e.target.value)} 
          placeholder="0.00"
        />

        <div className="pt-4 border-t border-gray-100">
          <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-widest">Allowance Settings</h4>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input 
              label="Amount ($)" 
              type="text" 
              inputMode="decimal"
              placeholder="0.00"
              value={formData.allowance} 
              onChange={e => handleCurrencyChange('allowance', e.target.value)} 
            />
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Interval</label>
              <select 
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                value={formData.interval}
                onChange={handleIntervalChange}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Scheduling Section */}
          {hasAllowance && (
            <div className={`grid gap-4 ${formData.interval === 'daily' ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {formData.interval === 'weekly' && (
                <div className="flex flex-col space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Day of Week</label>
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                    value={formData.allowance_day}
                    onChange={e => setFormData({...formData, allowance_day: e.target.value})}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {formData.interval === 'monthly' && (
                <div className="flex flex-col space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Day of Month</label>
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-sm h-[42px]"
                    value={formData.allowance_day}
                    onChange={e => setFormData({...formData, allowance_day: e.target.value})}
                  >
                    {Array.from({ length: 28 }, (_, i) => (
                      <option key={i + 1} value={(i + 1).toString()}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              )}

              <Input 
                label="Time" 
                type="time" 
                value={formData.allowance_time}
                onChange={e => setFormData({...formData, allowance_time: e.target.value})}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}